// src/pages/personal/analytics/FunnelSection/hooks/useFunnelData.ts
import { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useBusinessId } from '@/hooks/useBusinessId';
import { api } from '@/services/api';
import { 
  Period, 
  DateRange
} from '../../types/types';

// Добавляем типы данных для воронки
export type FunnelStage = 'awareness' | 'engagement' | 'leads' | 'sales' | 'retention';

export interface FunnelMetric {
  value: number;
  previous_value?: number;
  change: number;
  trend: 'up' | 'down' | 'neutral';
}

export interface FunnelStageData {
  stage: FunnelStage;
  name: string;
  color: string;
  value: number;
  previous_value: number;
  change: number;
  trend: 'up' | 'down' | 'neutral';
  conversion_from_previous?: number;
  metrics: Record<string, FunnelMetric>;
  data_missing?: boolean; // Добавлено свойство data_missing
}

export interface FunnelChannelDistribution {
  channel: string;
  name: string;
  awareness: number;
  engagement: number;
  leads: number;
  sales: number;
  retention: number;
  conversion_total: number;
}

export interface FunnelTimeSeriesPoint {
  period: string;
  awareness: number;
  engagement: number;
  leads: number;
  sales: number;
  retention: number;
}

export interface FunnelData {
  funnel_stages: FunnelStageData[];
  distribution_by_channel: FunnelChannelDistribution[];
  additional_metrics?: Record<string, FunnelMetric>;
  funnel_over_time: FunnelTimeSeriesPoint[];
  timezone?: string;
  has_data?: boolean;
}

interface UseFunnelDataParams {
  period: Period;
  channels?: string[];
  detailed?: boolean;
  dateRange?: DateRange;
}

interface UseFunnelDataReturn {
  data: FunnelData | null;
  isLoading: boolean;
  error: string | null;
  refreshData: () => Promise<void>;
}

export const useFunnelData = ({
  period,
  channels = [],
  detailed = true,
  dateRange
}: UseFunnelDataParams): UseFunnelDataReturn => {
  const { t } = useTranslation(['funnel', 'errors']);
  const { businessId, isLoading: isBusinessIdLoading } = useBusinessId();
  
  const [data, setData] = useState<FunnelData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Флаг для контроля активности компонента
  const isMounted = useRef(true);
  // Счетчик запросов для отладки
  const fetchCounter = useRef(0);
  // Флаг, указывающий, выполняется ли сейчас запрос
  const isRequestPending = useRef(false);

  const fetchData = useCallback(async () => {
    // Проверяем, не выполняется ли уже запрос
    if (isRequestPending.current) {
      console.log('Пропускаем запрос, так как предыдущий еще выполняется');
      return;
    }

    // Увеличиваем счетчик запросов для отладки
    fetchCounter.current += 1;
    const requestId = fetchCounter.current;
    
    console.log(`[${requestId}] Запрос данных воронки начат`);
    
    if (isBusinessIdLoading || !businessId || !isMounted.current) {
      console.log(`[${requestId}] Запрос отменен: businessId загружается или компонент размонтирован`);
      return;
    }

    // Проверяем валидность dateRange
    const isDateRangeValid = dateRange && 
      dateRange.startDate instanceof Date && !isNaN(dateRange.startDate.getTime()) &&
      dateRange.endDate instanceof Date && !isNaN(dateRange.endDate.getTime());
      
    // Устанавливаем флаг выполнения запроса
    isRequestPending.current = true;
    setIsLoading(true);
    setError(null);

    try {
      // Формируем параметры запроса
      const params: any = { 
        business: businessId.toString(),
        period: period,
        detailed: detailed.toString()
      };

      // Проверка, что период валидный
      if (!['day', 'week', 'month', 'quarter', 'year', 'custom'].includes(period)) {
        console.warn(`Некорректный период: ${period}, используем 'month' по умолчанию`);
        params.period = 'month';
      }

      // Добавляем фильтры по каналам если они указаны
      if (channels.length > 0) {
        params.channels = channels.join(',');
      }

      // Если есть диапазон дат и он валидный, добавляем его
      if (isDateRangeValid) {
        // Отправляем ISO строки для корректной обработки на бэкенде
        params.start_date = dateRange.startDate.toISOString();
        params.end_date = dateRange.endDate.toISOString();
        
        console.log(`Отправляем даты: start=${params.start_date}, end=${params.end_date}`);
      } else if (params.period === 'custom') {
        // Для custom периода обязательно нужны даты
        console.warn('Для периода custom требуется валидный dateRange, используем period=month');
        params.period = 'month';
      }
      
      console.log(`[${requestId}] Запрос к API с параметрами:`, params);
      
      const response = await api.get('/api/analytics/funnel/', params);

      // Проверяем активность компонента
      if (!isMounted.current) {
        console.log(`[${requestId}] Компонент размонтирован, отменяем обработку ответа`);
        return;
      }

      console.log(`[${requestId}] Получен ответ от API`, 
        response ? 'с данными' : 'без данных');

      if (response) {
        setData(response);
      } else {
        setError(t('errors:data_loading_error'));
      }
    } catch (error: any) {
      if (isMounted.current) {
        console.error(`[${requestId}] Ошибка загрузки данных:`, error);
        setError(error.message || t('errors:data_loading_error'));
      }
    } finally {
      // Сбрасываем флаг выполнения запроса
      isRequestPending.current = false;
      
      if (isMounted.current) {
        console.log(`[${requestId}] Завершение запроса`);
        setIsLoading(false);
      }
    }
  }, [businessId, isBusinessIdLoading, period, channels, detailed, dateRange, t]);

  // Эффект для инициализации данных
  useEffect(() => {
    console.log('Монтирование компонента, настройка параметров');
    isMounted.current = true;
    
    // Запускаем первичную загрузку данных только если у нас есть businessId
    if (businessId && !isBusinessIdLoading) {
      fetchData();
    }
    
    return () => {
      console.log('Размонтирование компонента');
      isMounted.current = false;
    };
  }, [fetchData, businessId, isBusinessIdLoading]);

  return {
    data,
    isLoading,
    error,
    refreshData: fetchData
  };
};